import { Link } from "react-router-dom";
import { Skeleton } from "@chakra-ui/react";
import { MdArrowUpward, MdArrowDownward } from "react-icons/md";

const trendStyles = {
  positive: "text-success-600 bg-success-50",
  negative: "text-danger-600 bg-danger-50",
  neutral: "text-gray-600 bg-gray-100",
};

function pickTrend(deltaPct, positiveIs = "up") {
  if (deltaPct === null || deltaPct === undefined) return "neutral";
  if (deltaPct === 0) return "neutral";
  const goodWhenUp = positiveIs === "up";
  if (deltaPct > 0) return goodWhenUp ? "positive" : "negative";
  return goodWhenUp ? "negative" : "positive";
}

function formatValue(value, format) {
  if (value === null || value === undefined) return "—";
  if (format === "currency") {
    return Number(value).toLocaleString("ar-SA", {
      maximumFractionDigits: 0,
    });
  }
  if (format === "decimal") {
    return Number(value).toLocaleString("ar-SA", {
      maximumFractionDigits: 1,
      minimumFractionDigits: 1,
    });
  }
  return Number(value).toLocaleString("ar-SA");
}

const KpiCard = ({
  icon,
  title,
  value,
  format = "number",
  deltaPct,
  previousValue,
  link,
  sparkline,
  loading = false,
  positiveIs = "up",
  suffix,
  subtitle,
}) => {
  const trend = pickTrend(deltaPct, positiveIs);
  const deltaText =
    deltaPct === null || deltaPct === undefined
      ? "—"
      : `${deltaPct > 0 ? "+" : ""}${deltaPct}%`;

  const body = (
    <div className="flex h-full min-h-[126px] flex-col gap-3 rounded-2xl border border-border-muted bg-white p-4 shadow-card transition hover:shadow-card-hover dark:border-navy-700 dark:bg-navy-800">
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-col gap-1">
          <p className="text-sm font-medium leading-5 text-gray-700 dark:text-gray-300">
            {title}
          </p>
          {subtitle ? (
            <p className="text-xs leading-4 text-gray-500">{subtitle}</p>
          ) : null}
        </div>
        {icon ? (
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-info-50 text-info-600 dark:bg-navy-700 dark:text-info-200">
            {icon}
          </span>
        ) : null}
      </div>

      {loading ? (
        <Skeleton height="34px" width="60%" />
      ) : (
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold text-navy-700 dark:text-white">
            {formatValue(value, format)}
          </span>
          {suffix ? (
            <span className="text-sm text-gray-500">{suffix}</span>
          ) : null}
        </div>
      )}

      <div className="mt-auto flex items-end justify-between gap-2">
        {deltaPct !== undefined ? (
          <span
            className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-semibold ${trendStyles[trend]}`}
          >
            {trend === "positive" ? (
              <MdArrowUpward className="h-3 w-3" />
            ) : trend === "negative" ? (
              <MdArrowDownward className="h-3 w-3" />
            ) : null}
            {deltaText}
            {previousValue !== undefined ? (
              <span className="text-gray-500 ms-1 font-normal">
                ({formatValue(previousValue, format)})
              </span>
            ) : null}
          </span>
        ) : (
          <span />
        )}
        {sparkline ? (
          <div className="h-10 w-24 shrink-0">{sparkline}</div>
        ) : null}
      </div>
    </div>
  );

  return link ? (
    <Link to={link} className="block h-full">
      {body}
    </Link>
  ) : (
    body
  );
};

export default KpiCard;
